/**
 * Global console error capture buffer.
 * Auto-initializes on import — patches console.error, window.onerror,
 * and unhandledrejection to capture the last 20 errors with timestamps.
 *
 * Used by ReportIssueButton to include recent errors in bug reports.
 */

interface CapturedLog {
  timestamp: string;
  type: 'error' | 'unhandled' | 'rejection';
  message: string;
}

const MAX_ENTRIES = 20;
const buffer: CapturedLog[] = [];

function addEntry(type: CapturedLog['type'], message: string) {
  buffer.push({
    timestamp: new Date().toISOString(),
    type,
    message: message.slice(0, 500),
  });
  if (buffer.length > MAX_ENTRIES) {
    buffer.shift();
  }
}

// Patch console.error
const originalConsoleError = console.error;
console.error = (...args: unknown[]) => {
  const message = args.map(a => {
    if (a instanceof Error) return `${a.message}\n${a.stack ?? ''}`;
    if (typeof a === 'string') return a;
    try { return JSON.stringify(a); } catch { return String(a); }
  }).join(' ');
  addEntry('error', message);
  originalConsoleError.apply(console, args);
};

// Capture uncaught errors
window.addEventListener('error', (event) => {
  const msg = event.error instanceof Error
    ? `${event.error.message}\n${event.error.stack ?? ''}`
    : event.message || 'Unknown error';
  addEntry('unhandled', `${msg} (${event.filename ?? ''}:${event.lineno ?? ''})`);
});

// Capture unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const msg = reason instanceof Error
    ? `${reason.message}\n${reason.stack ?? ''}`
    : String(reason);
  addEntry('rejection', msg);
});

/** Get all captured log entries (most recent last) */
export function getConsoleLogs(): CapturedLog[] {
  return [...buffer];
}

/** Format captured logs as a readable string for bug reports */
export function formatConsoleLogs(): string {
  if (buffer.length === 0) return '';
  return buffer.map(e =>
    `[${e.timestamp}] ${e.type.toUpperCase()}: ${e.message}`
  ).join('\n');
}
